import React from "react";
import { X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import DirtyDot from "./DirtyDot";
import CustomSelect from "../CustomSelect";
import { WIDGET_LOCALES, localeLabel } from "./widgetLocales";

const LanguageSection = ({ locale, locales, baseline, baselineLocales, onChange, onChangeLocales }) => {
  const { translate } = useLanguage();
  const isDirty =
    (locale || "pt") !== (baseline || "pt") ||
    JSON.stringify(locales || []) !== JSON.stringify(baselineLocales || []);

  const defaultLocale = locale || "pt";
  // Sem o filtro, trocar o idioma por omissão para um que já estava na lista
  // desenhava o mesmo idioma duas vezes — uma vez como chip fixo e outra
  // removível. O conjunto efectivo é `{locale} ∪ locales`, portanto o por
  // omissão já lá está por construção e a lista de baixo são só OS OUTROS.
  const extraLocales = (locales || []).filter((l) => l !== defaultLocale);
  const availableToAdd = WIDGET_LOCALES.filter(
    (l) => l.value !== defaultLocale && !extraLocales.includes(l.value)
  );

  const removeLocale = (v) => onChangeLocales(extraLocales.filter((x) => x !== v));
  const addLocale = (v) => {
    if (!v) return; // the placeholder option itself carries value=""
    onChangeLocales([...extraLocales, v]);
  };

  return (
    <div
      className="rounded-xl"
      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <h2 className="flex items-center gap-2 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("widgetLanguage") || "Widget interface language"}
          {isDirty && <DirtyDot />}
        </h2>
      </div>
      <div className="px-6 py-5">
        <CustomSelect value={defaultLocale} onChange={onChange} options={WIDGET_LOCALES} />
        <p className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
          {translate("widgetLanguageDescription") ||
            "Language of the widget's interface texts — not the language the assistant replies in."}
        </p>

        {/* Sub-título próprio, e não um bloco sem nome debaixo do <h2>: quem
            procurava "onde se escolhem os idiomas do widget" lia o cabeçalho
            "Idioma da interface do widget", dava a secção por ser só o dropdown
            e não chegava a ver as chips. Subordinado ao <h2> da secção, como os
            do ThemeSection — é a mesma secção, não uma nova. */}
        <h3
          className="mt-5 mb-2 text-xs font-medium uppercase tracking-wide"
          style={{ color: "var(--color-text-muted)" }}
        >
          {translate("widgetLocales") || "Widget languages"}
        </h3>

        <div className="flex flex-wrap gap-2" data-testid="widget-locale-chips">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
            style={{ backgroundColor: "var(--color-mint-muted)", color: "var(--color-mint)" }}
          >
            {localeLabel(defaultLocale)}
          </span>
          {extraLocales.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              style={{ backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-primary)" }}
            >
              {localeLabel(v)}
              <button
                type="button"
                onClick={() => removeLocale(v)}
                aria-label={`${translate("removeWidgetLocale") || "Remove language"}: ${localeLabel(v)}`}
                className="hover:opacity-70"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>

        {availableToAdd.length > 0 && (
          <div className="mt-2 max-w-xs" data-testid="widget-locale-add">
            <CustomSelect
              value=""
              onChange={addLocale}
              options={[
                { value: "", label: translate("addWidgetLocale") || "Add language" },
                ...availableToAdd,
              ]}
            />
          </div>
        )}

        <p className="mt-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
          {translate("widgetLocalesDescription") ||
            "The languages this widget serves. The embed script picks one with &language=. Each gets its own tab in Copy."}
        </p>
      </div>
    </div>
  );
};

export default LanguageSection;
